import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo } from "@/components/Brand";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { email_sent: bool } | null
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setResult({ email_sent: !!data.email_sent });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }

  const sent = result !== null;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2D4FB8 55%, #3B82F6 100%)" }}>
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at 30% 30%, black 0%, transparent 75%)",
          }}
        />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/30">
              <span className="font-display text-lg">B</span>
            </div>
            <div>
              <p className="font-display text-lg leading-none">Book-It</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">Smart booking platform</p>
            </div>
          </div>
          <div>
            <p className="overline text-white/70">Recuperación de cuenta</p>
            <h2 className="font-display text-4xl font-light mt-3 max-w-md leading-tight">
              Sin estrés.<br/>
              <span className="text-[#BFDBFE]">Recupera el acceso en minutos.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200">
          <CardContent className="p-8">
            <Link to="/" className="inline-block mb-6"><BrandLogo /></Link>
            {!sent ? (
              <>
                <h1 className="font-display text-3xl font-light tracking-tight">¿Olvidaste tu contraseña?</h1>
                <p className="text-slate-600 mt-2 text-sm">Ingresa el correo de tu cuenta y te enviaremos un enlace para restablecer tu contraseña.</p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Correo electrónico</Label>
                    <Input
                      id="email" type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com" data-testid="forgot-email-input"
                    />
                  </div>
                  {err && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-testid="forgot-error">
                      <p>{err}</p>
                      {err.toLowerCase().includes("cuenta") && (
                        <Link to="/signup" className="inline-block mt-1 text-xs underline font-medium" data-testid="forgot-error-signup">¿Aún no tienes negocio? Regístrate →</Link>
                      )}
                    </div>
                  )}
                  <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                    <Mail className="h-4 w-4 mr-2" /> {loading ? "Enviando…" : "Enviar enlace de recuperación"}
                  </Button>
                </form>
                <p className="text-sm text-slate-600 mt-6 text-center">
                  ¿Lo recordaste?{" "}
                  <Link to="/login" className="text-[hsl(var(--accent))] underline" data-testid="forgot-back-login">Volver a iniciar sesión</Link>
                </p>
              </>
            ) : (
              <div className="text-center py-6" data-testid="forgot-success">
                {result?.email_sent ? (
                  <>
                    <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center mb-4">
                      <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h1 className="font-display text-2xl font-light tracking-tight" data-testid="forgot-success-title">Correo enviado</h1>
                    <p className="text-slate-600 mt-3 text-sm">
                      Enviamos el enlace de recuperación a <span className="font-medium text-slate-900">{email}</span>. El enlace expira en <strong>60 minutos</strong>.
                    </p>
                    <p className="text-xs text-slate-500 mt-4">¿No lo ves? Revisa tu carpeta de spam.</p>
                    <Link to="/login" className="inline-block mt-6">
                      <Button variant="outline" data-testid="forgot-success-back">Volver al login</Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 grid place-items-center mb-4">
                      <AlertTriangle className="h-8 w-8 text-amber-600" />
                    </div>
                    <h1 className="font-display text-2xl font-light tracking-tight" data-testid="forgot-warning-title">No pudimos enviar el correo</h1>
                    <p className="text-slate-600 mt-3 text-sm">
                      La cuenta <span className="font-medium text-slate-900">{email}</span> existe, pero hubo un problema enviando el correo.
                      Esto suele ser un problema temporal del proveedor de email.
                    </p>
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 mt-4 text-xs text-amber-900 text-left">
                      <p className="font-semibold mb-1">¿Qué puedes hacer?</p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Espera unos minutos e intenta nuevamente.</li>
                        <li>Verifica que el correo no esté mal escrito.</li>
                        <li>Si persiste, contacta a soporte para que reseteen tu contraseña manualmente.</li>
                      </ul>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button variant="outline" onClick={() => { setResult(null); setErr(""); }} data-testid="forgot-retry-btn">
                        <RefreshCw className="h-3 w-3 mr-1" /> Reintentar
                      </Button>
                      <Link to="/login"><Button variant="ghost" data-testid="forgot-warning-back">Volver al login</Button></Link>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
